import React from "react";
import moment from "moment-timezone";
import { motion } from "framer-motion";
import { FaChevronRight, FaArrowLeft } from "react-icons/fa";
import { THEME } from "./theme";

// The batch overview shown right after a batch is picked, before the day grid
// — the same "read the batch, then proceed" beat the Batch Operations screen
// has. Metadata comes from getGrowthCurve's `others`; the progress line comes
// from the bulk day-status API and is omitted when that endpoint is down.

const pill = (bg) => ({
  background: bg,
  color: "#fff",
  border: "none",
  borderRadius: 20,
  padding: "9px 16px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
});

// Day 1 = start date. Clamped into [1, duration] so an over-run batch still
// reads as its final day rather than something past the schedule.
const currentDay = (startDate, duration) => {
  if (!startDate) return null;
  const elapsed = moment().tz("Asia/Singapore").diff(moment(startDate), "days") + 1;
  return Math.max(1, duration ? Math.min(elapsed, duration) : elapsed);
};

const BatchSummary = ({ batchName, growth, t, onProceed, onBack }) => {
  const o = growth?.ok ? growth.others : null;
  const duration = Number(o?.batchDuration) || 0;
  const day = currentDay(o?.startDate, duration);
  const sexed = o?.sexing === true || o?.sexing === "yes";

  const rows = o
    ? [
        [t.summaryBreed, o.breedName],
        [t.summaryBirds, o.numberofBirds],
        [t.summaryStart, o.startDate ? moment(o.startDate).format("YYYY-MM-DD") : null],
        [t.summaryCurrentDay, day != null ? `${t.dayPrefix} ${day}` : null],
        [t.summaryDuration, duration ? `${duration} ${String(t.dayPrefix).toLowerCase()}s` : null],
        [t.summarySexing, sexed ? t.summaryOn : t.summaryOff],
        [t.summaryStatus, o.status],
      ].filter(([, v]) => v !== null && v !== undefined && v !== "")
    : [];

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      {o ? (
        <div
          style={{
            background: "#f1f5ff",
            border: "1px solid #dbe6ff",
            borderRadius: 12,
            padding: "12px 14px",
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: THEME.ink, marginBottom: 8 }}>
            {batchName}
          </div>
          {rows.map(([label, value]) => (
            <div
              key={label}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                fontSize: 12.5,
                padding: "3px 0",
              }}
            >
              <span style={{ color: "#64748b" }}>{label}</span>
              <span style={{ fontWeight: 600, color: THEME.ink }}>{value}</span>
            </div>
          ))}
          {growth.statusOk && (
            <div
              style={{
                marginTop: 8,
                paddingTop: 8,
                borderTop: "1px dashed #c7d6f5",
                fontSize: 12.5,
                fontWeight: 700,
                color: THEME.blue,
              }}
            >
              {String(t.summaryProgress)
                .replace("{done}", growth.progress.done)
                .replace("{total}", growth.progress.total)}
            </div>
          )}
        </div>
      ) : (
        // Metadata failed, but nothing here blocks logging — say so and keep
        // Proceed enabled.
        <div style={{ fontSize: 13, color: THEME.grey, marginBottom: 12 }}>
          {t.summaryUnavailable}
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <motion.button
          type="button"
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.96 }}
          onClick={onProceed}
          style={pill(THEME.blue)}
        >
          {t.proceed} <FaChevronRight size={11} />
        </motion.button>
        <motion.button type="button" whileTap={{ scale: 0.95 }} onClick={onBack} style={pill(THEME.grey)}>
          <FaArrowLeft size={11} /> {t.back}
        </motion.button>
      </div>
    </motion.div>
  );
};

export default BatchSummary;
